import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router'; // Ensure RouterModule is imported
import { MatSelectCountryLangToken } from '@angular-material-extensions/select-country';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { JwtInterceptor } from './tf/Jwt-interceptor.interceptor';
import { AuthGuardGuard } from './tf/auth-guard.guard';
import { PermissionGuard } from './tf/permission.guard';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './Theme/shared/shared.module';
import { FooterComponent } from './Theme/shared/components/footer/footer.component';
import { AdminComponent } from './Theme/layout/admin/admin.component';
import { NavBarComponent } from './Theme/layout/admin/nav-bar/nav-bar.component';
import { NavLeftComponent } from './Theme/layout/admin/nav-bar/nav-left/nav-left.component';
import { NavRightComponent } from './Theme/layout/admin/nav-bar/nav-right/nav-right.component';
import { NavigationComponent } from './Theme/layout/admin/navigation/navigation.component';
import { NavLogoComponent } from './Theme/layout/admin/navigation/nav-logo/nav-logo.component';
import { NavContentComponent } from './Theme/layout/admin/navigation/nav-content/nav-content.component';
import { NavItemComponent } from './Theme/layout/admin/navigation/nav-content/nav-item/nav-item.component';
import { NavGroupComponent } from './Theme/layout/admin/navigation/nav-content/nav-group/nav-group.component';
import { NavCollapseComponent } from './Theme/layout/admin/navigation/nav-content/nav-collapse/nav-collapse.component';
import { ConfigurationComponent } from './Theme/layout/admin/configuration/configuration.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfigurationsService } from './Theme/layout/admin/configuration/service/configurations.service';
import { NavigationItem } from './Theme/layout/admin/navigation/navigation';
import { AppInitializerService } from './Theme/shared/service/app-initializer.service';
import { SearchBarComponent } from './customer/cf-customer/cf-home/search-bar/search-bar.component';
import { GuestComponent } from './Theme/layout/guest/guest.component';
import { ToggleFullScreenDirective } from './Theme/shared/full-screen/toggle-full-screen';
import { SpinnerInterceptor } from './Theme/shared/service/spinner.interceptor';
import { ToastrModule } from 'ngx-toastr';
import { MatAccordion } from '@angular/material/expansion';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { ErrorInterceptor } from './Theme/shared/_helpers/error.interceptor';
import { FeatherModule } from 'angular-feather';
import { allIcons } from 'angular-feather/icons';
import { Building } from './Theme/shared/icons/building.icon';
import { Languages } from './Theme/shared/icons/languages.icon';
import { NotificationGenericComponent } from './Theme/shared/components/notification-generic/notification-generic.component';
import { NotificationBellComponent } from './Theme/shared/components/notification-bell/notification-bell.component';
import { ProductSpecificationFilterSidebarComponent } from './customer/cf-customer/cf-product/product-specification-filter-sidebar/product-specification-filter-sidebar.component';
import { CompareNavbarComponent } from './Theme/shared/components/compare-navbar/compare-navbar.component';
import { provideEchartsCore } from 'ngx-echarts';
import { NotificationMobileToastService } from './Theme/shared/components/notification-mobile-toast/notification-mobile-toast.service';

export function initMobileTopBanner(service: NotificationMobileToastService): () => void {
  return () => {
    service.logViewportStatus('APP_INITIALIZER — service ready');
    console.warn('[MobileTopBanner] test in console: __testMobileTopBanner()');
  };
}

export function createTranslateLoader(http: HttpClient): any {
  return new TranslateHttpLoader(http);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http,
    './assets/i18n/',
    '.json');
}

export function initializeApp(configService: ConfigurationsService) {
  return (): Promise<any> => {
    return configService.loadConfig().toPromise();
  }
}

export function initializeTheme(appInitializerService: AppInitializerService) {
  return (): Promise<any> => {
    return appInitializerService.initializeApp();
  }
}

@NgModule({
  declarations: [
    AppComponent,
    AdminComponent,
    NavBarComponent,
    NavLeftComponent,
    NavRightComponent,
    NavigationComponent,
    NavLogoComponent,
    NavContentComponent,
    NavItemComponent,
    NavGroupComponent,
    NavCollapseComponent,
    ConfigurationComponent,
    GuestComponent,
    ToggleFullScreenDirective,

  ],
  imports: [
    CommonModule,
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule,
    SearchBarComponent,
    MatAccordion,
    MatBadgeModule,
    MatIcon,
    MatIconModule,
    MatDialogModule,
    MatExpansionModule,
    MatExpansionPanel,
    FeatherModule.pick({ ...allIcons, Building, Languages }),
    NotificationGenericComponent,
    NotificationBellComponent,
    ProductSpecificationFilterSidebarComponent,
    CompareNavbarComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    FooterComponent,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-left',
      preventDuplicates: true,
      closeButton: true
    }),
   

  ],
  providers: [
    provideEchartsCore({ echarts: () => import('echarts') }),
    AuthGuardGuard, PermissionGuard, NavigationItem,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
    { provide: MatSelectCountryLangToken, useValue: 'en' }, // Change 'en' to your desired language
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: initializeTheme, deps: [AppInitializerService], multi: true },
    { provide: APP_INITIALIZER, useFactory: initMobileTopBanner, deps: [NotificationMobileToastService], multi: true },

    // { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [ConfigurationsService], multi: true},
  ],

  bootstrap: [AppComponent],
  exports: []

})
export class AppModule { }
